import React, { useRef } from 'react';
import styled from 'styled-components';
import { Breadcrumbs, Parallax } from 'components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useLocation } from '@reach/router';

export const BlobWrapper = styled.div`
  bottom: 0;
  height: 3rem;
  margin-left: 3.5rem;
  position: absolute;
  width: 3rem;
`;

export const ButtonWrapper = styled.div`
  background-color: #fff;
  bottom: 0;
  position: absolute;
  view-transition-name: z-index-fix-1;
`;

export function Hero({
  heroImageOffset = -145,
  gradientPercentage = '87.5%',
  filter = 'contrast(120%) saturate(155%)',
  heroImage,
  heroImageAlt,
  heroImageTitle,
  removeProductNameFromPath,
  children,
  height
}) {
  return (
    <HeroContainer height={height}>
      <StyledParallax speed="1">
        <HeroImage
          image={getImage(heroImage)}
          filter={filter}
          $heroImageOffset={heroImageOffset}
          alt={heroImageAlt}
          title={heroImageTitle}
          loading="eager"
        />
      </StyledParallax>
      <BlackGradient $gradientPercentage={gradientPercentage} />
      <BrownGradient $gradientPercentage={gradientPercentage} />
      <Text>
        <StyledBreadcrumbs removeProductNameFromPath={removeProductNameFromPath} />
        {children}
      </Text>
    </HeroContainer>
  );
}

const Text = styled.div`
  column-gap: 1.25rem;
  display: grid;
  grid-column: 1/-1;
  grid-row: 1/-1;
  grid-template-columns: repeat(12, minmax(0, 100px));
  justify-content: center;

  h1,
  h2,
  p {
    color: #fff;
    grid-column: 1/-1;
    position: relative;
    left: 10px;
    z-index: 1;

    @media (width >= 640px) {
      grid-column: 2/12;
      left: 0;
    }
  }

  h1,
  h2 {
    font-size: 3rem;
    font-weight: 400;
    line-height: 140%;
    grid-row: 2;
  }

  h1 {
    margin-top: 4rem;

    @media (width >= 650px) {
      margin-top: 0.5rem;
    }

    @media (width >= 1100px) {
      font-size: 3rem !important;
    }
  }

  h2 {
    margin-top: 73px;

    @media (width >= 650px) {
      margin-top: 22px;
    }
  }

  p {
    grid-row: 3;
    line-height: 140%;
    margin-bottom: 4.125rem;
    max-width: 40.938rem;
  }
`;

const StyledParallax = styled(Parallax)`
  grid-column: 1/-1;
  grid-row: 1/-1;
  left: -10px;
  position: relative;
`;

export const HeroImage = styled(GatsbyImage)`
  filter: ${({ filter }) => filter};
  height: 440px;
  left: 0;
  position: absolute;
  top: ${({ $heroImageOffset }) => `${$heroImageOffset}px`};
  width: 100vw;
  z-index: 0;
`;

const BlackGradient = styled.div`
  background: ${({ $gradientPercentage }) =>
    `linear-gradient(330deg, transparent 3.44%, rgba(000 000 000 / 93.3%) ${$gradientPercentage})`};
  background-blend-mode: plus-darker;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const BrownGradient = styled.div`
  background: ${({ $gradientPercentage }) =>
    `linear-gradient(330deg, transparent 3.44%, rgba(73 10 12 / 10%) ${$gradientPercentage})`};
  background-blend-mode: plus-darker;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

export const HeroContainer = styled.section`
  align-content: start;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: repeat(3, auto);
  height: ${({ height }) => height || '190px'};
  overflow: hidden;
  padding: 0 0.625rem;
  position: relative;

  @media (width >= 650px) {
    height: ${({ height }) => height || '13.75rem'};
  }
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  color: #fff;
  grid-column: 2/8;
  grid-row: 1;
  left: 3px;
  margin-top: 33px;
  position: relative;
  z-index: 1;

  @media (width < 650px) {
    display: none;
  }
`;

export const Grid = styled.section`
  background-color: var(--connolly-grey);
  display: grid;
  grid-column-gap: 1.25rem;
  grid-template-columns: repeat(12, 1fr);
  margin: 0 auto;
  max-width: 90rem;
  padding: 0;

  @media (width >= 590px) {
    padding: 0 1.25rem;
  }

  @media (width >= 1100px) {
    padding: 0 0.625rem;
  }
`;

export const InnerGrid = styled.div`
  background-color: #fff;
  border-radius: 0.375rem;
  grid-column: 1/-1;
  grid-template-columns: subgrid;
  grid-template-rows: auto auto;
  margin: 0 auto;
  max-width: 590px;
  padding: 1.25rem 1.25rem 4.25rem;
  position: relative;
  row-gap: 1rem;
  top: 0;
  width: 100%;
  z-index: 1;

  @media (width >= 590px) {
    padding: 1.25rem 1.25rem 5.438rem;
  }

  @media (width >= 650px) {
    top: -2.313rem;
  }

  @media (width >= 900px) {
    margin: initial;
    display: grid;
    grid-column: 1/-1;
    grid-template-rows: 1fr;
    max-width: initial;
    padding: 1.875rem 1.25rem 37px;
  }

  @media (width >= 1000px) {
    padding: 1.875rem 1.875rem 37px;
  }

  @media (width >= 1100px) {
    grid-column: 2/12;
  }
`;

export const ProductDetails = styled.section`
  grid-column: 1/-1;
  padding: 2.4rem 0 3.75rem;
  position: relative;

  @media (width >= 650px) {
    padding: 2.125rem 0 3.75rem;
  }

  @media (width >= 900px) {
    grid-column: 7/-1;
    padding: 1.875rem 1rem 5.438rem;
  }

  @media (width >= 1000px) {
    padding: 2.125rem 3.125rem 3.75rem;
  }

  @media (width >= 1100px) {
    grid-column: 6/12;
  }

  h1,
  h2,
  h3 {
    color: var(--connolly-black);
  }

  h2 {
    font-size: 1.125rem;
  }

  h3 {
    font-family: 'Open Sans', 'Segoe UI', Tahoma, sans-serif;
    font-size: 1rem;
    font-weight: 400;
  }

  /* Available Sizes dropdown arrow */
  svg {
    width: 14px;
  }
`;

export const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.625em;
`;

export const Description = styled.p`
  margin-bottom: 2.6875rem;
`;

export const Specifications = styled.h2`
  margin-bottom: 1.3125rem;
`;

export const Spec = styled.div`
  margin-bottom: 1.4375rem;
`;
